import { useCallback, useEffect, useState } from "react";
import { api, unwrapObject } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, PackageMinus, Undo2 } from "lucide-react";
import { qty, type MaterialData, type MaterialRow, type WorkOrderRecord } from "@/types/production";
import { pickedToItems, RollPicker, type PickedRoll } from "./RollPicker";

const field = "w-full h-10 rounded-lg border border-input bg-background px-3 text-sm focus:outline-hidden focus:ring-2 focus:ring-ring";

/** Material taken out of stock for a work order, what has gone back, and what is still open */
export function MaterialSection({ w, canUpdate, reloadKey, onChanged }: { w: WorkOrderRecord; canUpdate: boolean; reloadKey: number; onChanged: () => void }) {
  const [data, setData] = useState<MaterialData | null>(null);
  const [issueOpen, setIssueOpen] = useState(false);
  const [settleOpen, setSettleOpen] = useState(false);
  const active = w.work_order_status === "PLANNED" || w.work_order_status === "IN_PROGRESS";

  const load = useCallback(async () => {
    try {
      setData(unwrapObject<MaterialData>(await api.get(`/api/ProductionStock/Materials/${w.id}`)));
    } catch {
      setData({ rows: [], totals: [], open_count: 0 });
    }
  }, [w.id]);
  useEffect(() => {
    void load();
  }, [load, reloadKey]);
  const changed = () => {
    void load();
    onChanged();
  };

  if (!data) return null;
  const openRows = data.rows.filter((r) => r.material_status === "ISSUED");
  if (!data.rows.length && !(canUpdate && active)) return null;

  return (
    <div className="bg-card border rounded-2xl overflow-hidden">
      <div className="px-4 py-3 border-b flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h3 className="text-sm font-bold">Material</h3>
          <p className="text-[11px] text-muted-foreground">
            {data.totals.length ? data.totals.map((t) => `${qty(t.issued, t.unit)} issued · ${qty(t.returned, t.unit)} back · ${qty(t.used, t.unit)} used`).join("  |  ") : "Yarn, greige cloth or other rolls taken from stock for this work order"}
          </p>
        </div>
        {canUpdate && (
          <div className="flex gap-2">
            {openRows.length > 0 && (
              <Button size="sm" variant="outline" className="h-8 text-xs gap-1" onClick={() => setSettleOpen(true)}>
                <Undo2 className="size-3.5" /> Settle ({openRows.length})
              </Button>
            )}
            {active && (
              <Button size="sm" variant="outline" className="h-8 text-xs gap-1" onClick={() => setIssueOpen(true)}>
                <PackageMinus className="size-3.5" /> Issue material
              </Button>
            )}
          </div>
        )}
      </div>
      {data.rows.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wider text-muted-foreground border-b">
                <th className="px-4 py-2 font-semibold">Material</th>
                <th className="px-2 py-2 font-semibold">Roll · lot</th>
                <th className="px-2 py-2 font-semibold">For</th>
                <th className="px-2 py-2 font-semibold text-right">Issued</th>
                <th className="px-2 py-2 font-semibold text-right">Back</th>
                <th className="px-4 py-2 font-semibold">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {data.rows.map((r) => (
                <tr key={r.id}>
                  <td className="px-4 py-2">{r.product_name}{r.variant_name ? ` · ${r.variant_name}` : ""}</td>
                  <td className="px-2 py-2 text-xs text-muted-foreground">{r.unit_no} · {r.lot_number}</td>
                  <td className="px-2 py-2 text-xs">{r.challan_number ? <span className="font-mono">{r.challan_number}</span> : r.stage_name || "Work order"}</td>
                  <td className="px-2 py-2 text-right">{qty(r.quantity, r.unit)}</td>
                  <td className="px-2 py-2 text-right">{r.material_status === "SETTLED" ? qty(r.returned_qty, r.unit) : "-"}</td>
                  <td className="px-4 py-2 text-xs">{r.material_status === "ISSUED" ? <span className="font-semibold text-amber-600">With production</span> : <span className="text-muted-foreground">Settled</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <IssueMaterialDialog w={w} open={issueOpen} onClose={() => setIssueOpen(false)} onSaved={changed} />
      <SettleMaterialDialog w={w} open={settleOpen} rows={openRows} onClose={() => setSettleOpen(false)} onSaved={changed} />
    </div>
  );
}

function IssueMaterialDialog({ w, open, onClose, onSaved }: { w: WorkOrderRecord; open: boolean; onClose: () => void; onSaved: () => void }) {
  const { success, error } = useToast();
  const [picked, setPicked] = useState<PickedRoll[]>([]);
  const [stageId, setStageId] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) {
      setPicked([]);
      setStageId("");
    }
  }, [open]);
  const open_stages = (w.stages || []).filter((s) => s.stage_status === "PENDING" || s.stage_status === "IN_PROGRESS");
  const submit = async () => {
    if (!picked.length) return error("Validation Error", "Choose at least one roll");
    setSaving(true);
    try {
      await api.post(`/api/ProductionStock/IssueMaterial/${w.id}`, { items: pickedToItems(picked), ...(stageId ? { stage_id: stageId } : {}) });
      success("Material issued", `${picked.length} roll${picked.length === 1 ? "" : "s"} taken out of stock`);
      onSaved();
      onClose();
    } catch (err: any) {
      error("Could not issue", err.message || "Please try again");
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Issue material to {w.wo_number}</DialogTitle>
          <DialogDescription>The rolls leave stock now. Later, say how much of each goes back.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-1">
          <div className="space-y-1.5">
            <Label>For stage (optional)</Label>
            <select className={field} value={stageId} onChange={(e) => setStageId(e.target.value)}>
              <option value="">Whole work order</option>
              {open_stages.map((s) => (
                <option key={s.id} value={s.id}>{s.process_name}</option>
              ))}
            </select>
          </div>
          <RollPicker picked={picked} onChange={setPicked} />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving || !picked.length}>{saving && <Loader2 className="size-4 animate-spin mr-1.5" />}Issue</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SettleMaterialDialog({ w, open, rows, onClose, onSaved }: { w: WorkOrderRecord; open: boolean; rows: MaterialRow[]; onClose: () => void; onSaved: () => void }) {
  const { success, error } = useToast();
  const [back, setBack] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) setBack(Object.fromEntries(rows.map((r) => [r.id, "0"])));
  }, [open]);
  const submit = async () => {
    const items = rows.map((r) => ({ material_id: r.id, returned_qty: Number(back[r.id] || 0) }));
    const bad = rows.find((r, i) => !(items[i]!.returned_qty >= 0) || items[i]!.returned_qty > Number(r.quantity));
    if (bad) return error("Validation Error", `Roll ${bad.unit_no}: enter 0 to ${bad.quantity} ${bad.unit}`);
    setSaving(true);
    try {
      await api.post(`/api/ProductionStock/SettleMaterial/${w.id}`, { items });
      success("Material settled", "What was returned is back in stock");
      onSaved();
      onClose();
    } catch (err: any) {
      error("Could not settle", err.message || "Please try again");
    } finally {
      setSaving(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Settle material</DialogTitle>
          <DialogDescription>For each roll, enter how much goes back to stock. Whatever does not go back counts as used.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2 py-1 text-sm">
          <div className="flex justify-end">
            <Button type="button" variant="ghost" size="sm" className="h-7 text-xs" onClick={() => setBack(Object.fromEntries(rows.map((r) => [r.id, "0"])))}>All used</Button>
          </div>
          {rows.map((r) => (
            <div key={r.id} className="flex items-center gap-2">
              <div className="flex-1 min-w-0">
                <div className="truncate">{r.product_name}</div>
                <div className="text-[11px] text-muted-foreground">Roll {r.unit_no} · lot {r.lot_number} · {qty(r.quantity, r.unit)} issued{r.challan_number ? ` · ${r.challan_number}` : ""}</div>
              </div>
              <Input inputMode="decimal" value={back[r.id] ?? ""} onChange={(e) => setBack({ ...back, [r.id]: e.target.value })} className="h-9 w-24 text-sm" />
              <button type="button" className="text-[11px] text-primary hover:underline w-14" onClick={() => setBack({ ...back, [r.id]: String(Number(r.quantity)) })}>All back</button>
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={saving}>{saving && <Loader2 className="size-4 animate-spin mr-1.5" />}Settle</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
